import MomentUtils from '@date-io/moment';
import Framework, { LoadingHandler, shapes } from '@greenville/framework';
import { Box, Button, Grid, Paper, TextField, Typography } from '@material-ui/core';
import { ThemeProvider, createTheme, withStyles } from '@material-ui/core/styles';
import { KeyboardDatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import Checkbox from '@mui/material/Checkbox';
import FormHelperText from '@mui/material/FormHelperText';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import axios from 'axios';
import _ from 'lodash';
import { toJS } from 'mobx';
import { inject, observer } from 'mobx-react';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import Loader from '../../../../common/Loader';
import ChatgptConfig from '../../../../common/config/ChatgptConfig';
import * as constants from '../../../../common/constants';
import env from '../../../../common/env';
import utils from '../../../../common/utils';
import AutocompleteComponent from '../../common/components/Autocomplete';
import ChatgptAdmin from '../../models/ChatgptAdmin';
import InternalUsersList from '../../models/InternalUsersList';
import TenantConfigModel from '../../models/TenantConfig';
import UserFeedbackModal from '../../models/UserFeedback';
import ChannelsDataGrid from './datagrid/ChannelsDataGrid';

const materialTheme = createTheme({
  overrides: {
    MuiPickersToolbar: {
      toolbar: {
        backgroundColor: constants.PEARSON_PRIMARY_COLOR
      }
    },
    MuiPickersYear: {
      root: {
        '&:hover': {
          backgroundColor: '#d2cfc4'
        }
      },
      yearSelected: {
        color: constants.PEARSON_PRIMARY_COLOR
      },
      current: {
        color: constants.PEARSON_PRIMARY_COLOR
      }
    },
    MuiPickersDay: {
      day: {
        '&:hover': {
          backgroundColor: '#d2cfc4'
        }
      },
      daySelected: {
        backgroundColor: constants.PEARSON_PRIMARY_COLOR,
        '&:hover': {
          backgroundColor: constants.PEARSON_PRIMARY_COLOR
        }
      },
      current: {
        color: constants.PEARSON_PRIMARY_COLOR
      }
    }
  }
});

const themes = createTheme({
  palette: {
    primary: {
      light: '#047a9c',
      main: '#005d83',
      dark: '#003558',
      contrastText: '#ffffff'
    }
  },
  overrides: {
    MuiButton: {
      root: {
        borderRadius: 30
      }
    }
  }
});

const styles = (theme) => ({
  datePicker: {
    marginTop: 'inherit'
  },
  buttonGroupStyle: {
    justifyContent: 'center'
  },
  blurStyle: {
    position: 'absolute',
    background: 'rgb(255 255 255 / 20%)',
    top: '0',
    width: '93%',
    height: '100%',
    backdropFilter: 'blur(0.3px)',
    zIndex: '9999'
  },
  buttonStyle: {
    width: '120px',
    margin: '10px'
  },
  container: {
    marginBottom: 20
  },
  paper: {
    padding: theme.spacing(2),
    textAlign: 'left',
    color: theme.palette.text.secondary
  },
  title: {
    marginTop: -20
  },
  labelWidht: {
    width: '380px'
  },
  boxWidth: {
    paddingTop: theme.spacing(1),
    paddingRight: theme.spacing(1),
    paddingLeft: theme.spacing(4)
  },
  button: {
    paddingTop: theme.spacing(2),
    marginBottom: '-3em'
  },
  root: {
    top: '12%'
  },
  msgStyle: {
    fontSize: 'large',
    width: '100%',
    '& > * + *': {
      marginTop: theme.spacing(0)
    },
    zIndex: 9999
  }
});

const ChannelsFeedback = (props) => {
  const { classes, chatgptAdmin, internalUsers, userFeedback, tenantConfig } = props;
  const defaultSearch = {
    bookId: '',
    userId: '',
    email: '',
    startDateTime: '',
    endDateTime: '',
    type: '',
    startDateTimeError: false,
    endDateTimeError: false
  };
  const { isNextPageKey } = userFeedback;
  const feedbackResponseRef = useRef([]);
  const [searchFields, setSearchFields] = useState(defaultSearch);
  const [startDateTimeError, setStartDateTimeError] = useState(false);
  const [endDateTimeError, setEndDateTimeError] = useState(false);
  const [endDisabled, setEndDisabled] = useState(true);
  const [groupsData, setGroupsData] = useState({ types: [], channelsList: [] });
  const [userToken, setUserToken] = useState('');
  const [inputValue, setInputValue] = useState('');
  const [isUserIdExcluded, setIsUserIdExcluded] = useState(true);
  const [feedbackResponse, setFeedbackResponse] = useState([]);
  const [apiLoading, setApiLoading] = useState(false);
  const [isbookIdRequired, setBookIdRequired] = useState(false);

  useEffect(() => {
    const token = utils.getToken();
    setUserToken(token);
    setSearchFields((prevFields) => ({
      ...prevFields,
      bookId: constants.DEFAULT_CHANNELS_ID
    }));
  }, []);

  const getChannelsData = async () => {
    const responseValues = JSON.parse(JSON.stringify(toJS(chatgptAdmin)));
    if (!_.isEmpty(responseValues?.groups)) {
      const typesData = responseValues?.groups.filter((item) => item.name === 'Types')[0];
      const channelsNames = responseValues?.groups.filter((item) => item.name === 'channelsName')[0];
      setGroupsData({ types: typesData, channelsList: channelsNames });
    } else {
      const headers = {
        Authorization: `Bearer ${userToken}`
      };
      const response = await axios.get(`${env.EVERGREEN_API_BASE_URL}${constants.GET_CHANNELS_DATA_URL}`, { headers });
      if (response && response.data && response.data.data) {
        const { groups } = response.data.data;
        if (groups && groups.length > 0) {
          const typesData = groups.filter((item) => item.name === 'Types')[0];
          const channelsNames = groups.filter((item) => item.name === 'channelsName')[0];
          setGroupsData({ types: typesData, channelsList: channelsNames });
        }
        Framework.getStoreRegistry().getStore('chatgptAdmin').setResponse(response.data);
      }
    }
  };

  const getS3InternalUsersList = async () => {
    const usersData = JSON.parse(JSON.stringify(toJS(internalUsers)));
    if (usersData && usersData?.userId.length === 0) {
      const headers = {
        Authorization: `Bearer ${userToken}`
      };
      const payload = {
        itemPath: constants.S3_EXCLUDED_USERS_LIST_PATH
      };
      const response = await axios.post(`${env.EVERGREEN_API_BASE_URL}${constants.GET_S3_USERS_LIST_URL}`, payload, {
        headers
      });
      if (response && response.data && response.data.data) {
        Framework.getStoreRegistry().getStore('internalUsers').setResponse(response.data.data[0]);
      }
    }
  };

  useEffect(() => {
    if (!_.isEmpty(userToken)) {
      getChannelsData();
      getS3InternalUsersList();
    }
  }, [userToken]);

  useEffect(() => {
    const { data } = userFeedback;
    if (!data) return;
    /* eslint-disable camelcase */
    Framework.getEventManager().on(constants.SET_USER_FEEDBACK_DATA, () => {
      const resData = toJS(data);
      if (_.isEmpty(isNextPageKey?.ExclusiveStartKey?.feedbackId?.S)) {
        feedbackResponseRef.current = [];
      }
      const combineData = [...feedbackResponseRef.current, ...resData];
      const sortedData = combineData?.sort((x, y) => new Date(y.serverDateTime) - new Date(x.serverDateTime));
      setFeedbackResponse(sortedData);
      feedbackResponseRef.current = sortedData;
      setApiLoading(false);
    });
  }, [userFeedback]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSearchFields((prev) => ({
      ...prev,
      [name]: value
    }));
    setBookIdRequired(false);
  };

  const handleDateChange = (date, name) => {
    setSearchFields((prevSearchFields) => {
      const fieldChanged = {};
      fieldChanged[name] = moment(date).format(constants.MM_DD_YYYY_FORMAT);

      const newSearchFields = { ...prevSearchFields };
      const newState = {
        [`${name}Error`]: false,
        endDisabled: false
      };

      if (date === null && fieldChanged[name] === 'Invalid date') {
        newSearchFields[name] = '';
      } else if (fieldChanged[name] === 'Invalid date') {
        newState[`${name}Error`] = true;
        /* eslint-disable-next-line no-underscore-dangle */
        newSearchFields[name] = date._i;
      } else {
        newSearchFields[name] = fieldChanged[name];
      }

      if (name === 'startDateTime' && fieldChanged.startDateTime === 'Invalid date') {
        newState.endDisabled = true;
        newSearchFields.endDateTime = '';
        newState.endDateTimeError = false;
        setEndDateTimeError(false);
      }

      if (name === 'startDateTime') {
        setStartDateTimeError(newState.startDateTimeError);
      } else if (name === 'endDateTime') {
        setEndDateTimeError(newState.endDateTimeError);
      }
      setEndDisabled(newState.endDisabled);

      return newSearchFields;
    });
  };

  const handleReset = () => {
    setSearchFields(defaultSearch);
    setEndDisabled(true);
    setStartDateTimeError(false);
    setEndDateTimeError(false);
    setBookIdRequired(false);
    setInputValue('');
  };

  const onAutoCompleteChange = (event, newValue) => {
    setSearchFields((prevFields) => ({
      ...prevFields,
      bookId: newValue !== null ? newValue.id : ''
    }));
    setBookIdRequired(false);
  };

  const onInputChange = (event, newInputValue) => {
    setInputValue(newInputValue);
  };

  const handleSearch = (customPayload) => {
    setApiLoading(true);
    if (startDateTimeError || endDateTimeError) {
      return;
    }
    const tentId = utils.getTenantId('channels', tenantConfig);
    const baseParams = { ...searchFields, tenantId: tentId };
    const payload = { ...baseParams, ...customPayload };
    if (payload && (payload.bookId || payload.email || payload.userId || payload.type)) {
      userFeedback.fetch(payload);
    } else {
      setBookIdRequired(true);
    }
  };

  const handleAscending = (dir, value) => {
    const sorted = [...feedbackResponse].sort((a, b) => {
      if (a[value] && b[value]) {
        const aVal = a[value].toLowerCase();
        const bVal = b[value].toLowerCase();
        if (dir === 'asc') {
          return aVal > bVal ? 1 : -1;
        }
        return aVal < bVal ? 1 : -1;
      }
      return 0;
    });

    setFeedbackResponse(sorted);
  };

  const renderChannelsFeedback = () => {
    return feedbackResponse && feedbackResponse.length > 0 ? (
      <Box>
        <br />
        <ChannelsDataGrid
          channelsUtilityData={feedbackResponse}
          handleAscending={handleAscending}
          isNextPageKey={isNextPageKey}
          handleSearch={handleSearch}
          isUserIdExcluded={isUserIdExcluded}
          setUserId={() => {}}
          userIdChecked={false}
          isUserFeedBack
        />
      </Box>
    ) : (
      <>
        <br />
        <Paper>
          <br />
          <Box>
            <br />
            <Typography
              variant="h4"
              align="center"
              gutterBottom
              spacing={9}
              className={classes.title}
              style={{ p: 5, mt: 25 }}
            >
              {constants.CHAT_FETCH}
            </Typography>
          </Box>
        </Paper>
      </>
    );
  };

  return (
    <>
      <Paper>
        <Box sx={{ flexGrow: 1 }}>
          <Box sx={{ p: 2 }}>
            <Typography variant="h5" align="center" gutterBottom spacing={9}>
              {constants.CHAT_CRITERIA_TEXT}
            </Typography>
          </Box>
          <Grid container direction="row">
            <Grid item xs={1} />
            <Grid item xs={10}>
              <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'space-between', p: '0px 33px' }}>
                {groupsData?.channelsList && groupsData?.channelsList?.properties && (
                  <FormControl sx={{ minWidth: '32%', maxWidth: '90%' }} size="small">
                    <AutocompleteComponent
                      options={groupsData?.channelsList?.properties}
                      onAutoCompleteChange={onAutoCompleteChange}
                      onInputChange={onInputChange}
                      stateVal={inputValue}
                      errorVal={isbookIdRequired}
                      name="bookId"
                      label="Channels Id"
                      componentDefaultValue={{
                        title: constants.DEFAULT_CHANNELS_ID,
                        bookId: constants.DEFAULT_CHANNELS_ID,
                        id: 1
                      }}
                    />
                    {isbookIdRequired && (
                      <FormHelperText style={{ color: 'red' }}>Please select any Channels Id</FormHelperText>
                    )}
                  </FormControl>
                )}
                <MuiPickersUtilsProvider utils={MomentUtils}>
                  <ThemeProvider theme={materialTheme}>
                    <KeyboardDatePicker
                      className={classes.datePicker}
                      format={constants.MM_DD_YYYY_FORMAT}
                      autoOk
                      size="small"
                      variant="inline"
                      style={{ minWidth: '32%' }}
                      inputVariant="outlined"
                      disableFuture
                      value={searchFields.startDateTime || null}
                      label="Start Date"
                      onChange={(date) => handleDateChange(date, 'startDateTime')}
                      KeyboardButtonProps={{ 'aria-label': 'change date' }}
                      helperText={startDateTimeError ? 'Invalid Date Format' : ''}
                      error={startDateTimeError}
                    />
                    <KeyboardDatePicker
                      className={classes.datePicker}
                      format={constants.MM_DD_YYYY_FORMAT}
                      autoOk
                      variant="inline"
                      size="small"
                      style={{ minWidth: '32%' }}
                      inputVariant="outlined"
                      disableFuture
                      value={searchFields.endDateTime || null}
                      label="End Date"
                      minDate={searchFields.startDateTime || undefined}
                      onChange={(date) => handleDateChange(date, 'endDateTime')}
                      disabled={endDisabled}
                      KeyboardButtonProps={{ 'aria-label': 'change date' }}
                      helperText={endDateTimeError ? 'Invalid Date Format' : ''}
                      error={endDateTimeError}
                    />
                  </ThemeProvider>
                </MuiPickersUtilsProvider>
              </Box>
            </Grid>
            <Grid item xs={1} />
          </Grid>
          <br />
          <Grid container direction="row">
            <Grid item xs={1} />
            <Grid item xs={10} direction="row">
              <Box sx={{ display: 'flex', justifyContent: 'space-between', p: '0px 32px' }}>
                <TextField
                  label="User ID"
                  margin="dense"
                  name="userId"
                  onChange={handleChange}
                  style={{ minWidth: '32%' }}
                  value={searchFields.userId}
                  variant="outlined"
                />
                <TextField
                  label="Email ID"
                  margin="dense"
                  type="email"
                  style={{ minWidth: '32%' }}
                  name="email"
                  onChange={handleChange}
                  value={searchFields.email}
                  variant="outlined"
                />
                <FormControl sx={{ mt: 1, width: '32%' }} size="small">
                  <InputLabel>Type</InputLabel>
                  <Select label="Type" name="type" onChange={handleChange} value={searchFields.type || ''}>
                    <MenuItem disabled value="">
                      <em>{constants.SELECT_PLACEHOLDER_TYPE}</em>
                    </MenuItem>
                    {ChatgptConfig.channelsTypeData.map((filter) => (
                      <MenuItem key={filter.name} value={filter.name}>
                        {filter.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Grid>
            <Grid item xs={1} />
          </Grid>
          <br />
        </Box>
        <Box>
          <Grid container direction="row" justifyContent="center">
            <FormControl size="small">
              <FormControlLabel
                control={
                  <Checkbox
                    onChange={(e) => setIsUserIdExcluded(e.target.checked)}
                    name="excludeUserId"
                    checked={isUserIdExcluded}
                  />
                }
                style={{
                  alignItems: 'center'
                }}
                labelPlacement="end"
                label="Filter Internal Users"
              />
            </FormControl>
          </Grid>
        </Box>

        <br />
        <Box>
          <Grid container justify="space-evenly" className={classes.buttonGroupStyle}>
            <ThemeProvider theme={themes}>
              <Button
                variant="contained"
                size="small"
                color="primary"
                onClick={() => handleSearch()}
                className={classes.buttonStyle}
              >
                {constants.CHAT_SEARCH}
              </Button>
              <Button
                variant="contained"
                size="small"
                color="primary"
                onClick={handleReset}
                className={classes.buttonStyle}
              >
                {constants.CHAT_RESET}
              </Button>
            </ThemeProvider>
          </Grid>
          <br />
        </Box>
        <LoadingHandler loading={apiLoading} loadingContent={<Loader />} content={renderChannelsFeedback()} />
      </Paper>
    </>
  );
};

ChannelsFeedback.propTypes = {
  classes: PropTypes.object.isRequired,
  chatgptAdmin: shapes.modelOf(ChatgptAdmin).isRequired,
  internalUsers: shapes.modelOf(InternalUsersList).isRequired,
  userFeedback: shapes.modelOf(UserFeedbackModal).isRequired,
  tenantConfig: shapes.modelOf(TenantConfigModel).isRequired
};

export default observer(
  inject('chatgptAdmin', 'internalUsers', 'userFeedback', 'tenantConfig')(withStyles(styles)(ChannelsFeedback))
);
